import { createMarketApiFetch } from "@/lib/marketApiFetch";
import {
  getReceivableOrderId,
  isReceivableAddressItem,
  isReceivableAnnouncementItem,
  isReceivableSlipItem,
  type ReceivableListItem,
} from "@/lib/pendingOrders";
import { isReceivableArchivedDisplayStatus } from "@/lib/receivableDisplayStatus";

const marketApiFetch = createMarketApiFetch();

export type UnmatchCandidate = {
  allocation_id: string;
  payment_id: string;
  amount: string;
  sender_name?: string;
};

export type UnmatchImpact = {
  ok?: boolean;
  reason?: string;
  allocation_id?: string;
  payment_id?: string;
  amount?: string;
  sender_name?: string;
  summary_lines?: string[];
  payment_after?: Record<string, string>;
  receivable_after?: Record<string, string>;
  candidates?: UnmatchCandidate[];
};

export type EditAmountImpact = {
  ok?: boolean;
  reason?: string;
  blocked?: boolean;
  requires_release?: boolean;
  shortfall?: string;
  excess?: string;
  release_candidates?: Array<{
    allocation_id: string;
    payment_id: string;
    amount: string;
    sender_name?: string;
  }>;
  current?: {
    amount: string;
    amount_paid: string;
    amount_remaining: string;
    status: string;
    revision: number;
  };
  proposed?: {
    amount: string;
    amount_paid?: string;
    amount_remaining?: string;
    status?: string;
  };
  summary_lines?: string[];
};

function parseStoredAmount(raw: string): number {
  const normalized = raw.replace(/[^\d.,-]/g, "").replace(/,/g, "").trim();
  if (!normalized) return 0;
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function parseAmountInput(raw: string): number | null {
  const normalized = raw.replace(/,/g, "").trim();
  if (!normalized) return null;
  const parsed = Number.parseFloat(normalized.replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : null;
}

export function isEditAmountBlockedLocally(
  newAmountRaw: string,
  amountPaid: number,
): boolean {
  const parsed = parseAmountInput(newAmountRaw);
  if (parsed === null || parsed <= 0) return false;
  return parsed < amountPaid;
}

export function buildEditAmountBlockedMessage(
  newAmount: number,
  amountPaid: number,
): string {
  const shortfall = amountPaid - newAmount;
  return `จับคู่แล้ว ${amountPaid.toLocaleString("th-TH")} บาท ลดเป็น ${newAmount.toLocaleString("th-TH")} ไม่ได้ — ยกเลิกจับคู่ก่อนอย่างน้อย ${shortfall.toLocaleString("th-TH")} บาท`;
}

export function isEditAmountInvalid(newAmountRaw: string): boolean {
  const parsed = parseAmountInput(newAmountRaw);
  return parsed === null || parsed <= 0;
}

export function buildEditAmountInvalidMessage(): string {
  return "กรุณาระบุยอดแจ้งมากกว่า 0 บาท";
}

const TECHNICAL_EDIT_AMOUNT_ERRORS = new Set([
  "amount_must_be_positive",
  "invalid_amount",
  "receivable_not_found",
]);

export function formatEditAmountErrorMessage(
  message: string | null | undefined,
): string | null {
  const trimmed = message?.trim();
  if (!trimmed) return null;
  if (TECHNICAL_EDIT_AMOUNT_ERRORS.has(trimmed)) {
    return buildEditAmountInvalidMessage();
  }
  if (/new_amount must be > 0/i.test(trimmed)) {
    return buildEditAmountInvalidMessage();
  }
  if (/amount_must_be_positive|invalid_amount/i.test(trimmed)) {
    return buildEditAmountInvalidMessage();
  }
  if (/revision conflict/i.test(trimmed)) {
    return "รายการนี้ถูกแก้ไปแล้วจากที่อื่น — ลองบันทึกอีกครั้ง";
  }
  return trimmed;
}

export function formatUnmatchErrorMessage(
  message: string | null | undefined,
): string | null {
  const trimmed = message?.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("multiple_allocations")) {
    return "มีการจับคู่หลายรายการ — กรุณาเลือกรายการที่ต้องการยกเลิกก่อน";
  }
  if (
    trimmed === "no_active_allocation" ||
    trimmed.startsWith("no_active_allocation")
  ) {
    return "ไม่มีการจับคู้อยู่แล้ว — รีเฟรชรายการแล้วลองใหม่";
  }
  return trimmed;
}

/** Impact preview with no active match — expected after the last unmatch. */
export function isNoActiveAllocationImpact(
  impact: Pick<UnmatchImpact, "ok" | "reason" | "candidates">,
): boolean {
  return impact.reason === "no_active_allocation";
}

function pickField(item: ReceivableListItem, keys: string[]): string {
  for (const key of keys) {
    const value = item.fields.find((field) => field.key === key)?.value?.trim();
    if (value && value !== "—") return value;
  }
  return "";
}

export function pickReceivableRevision(item: ReceivableListItem): number {
  const raw = pickField(item, ["revision", "receivable_revision"]);
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function pickPaymentRevision(item: ReceivableListItem): number {
  const raw = pickField(item, [
    "payment_revision",
    "revision",
    "receivable_revision",
  ]);
  // Prefer explicit payment_revision when present
  const explicit = pickField(item, ["payment_revision"]);
  if (explicit) {
    const parsed = Number.parseInt(explicit, 10);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function pickAmountPaid(item: ReceivableListItem): number {
  const raw = pickField(item, ["amount_paid", "amount_allocated"]);
  return parseStoredAmount(raw);
}

export function pickAmountOriginal(item: ReceivableListItem): number {
  const raw =
    pickField(item, ["amount_original", "outstanding_amount", "amount"]) ||
    item.amountLabel?.replace(/[^\d.,]/g, "").replace(/,/g, "") ||
    "";
  return parseStoredAmount(raw);
}

function pickPgReceivableId(item: ReceivableListItem): string | null {
  const fromItem = item.receivableId?.trim();
  if (fromItem) return fromItem;
  const fromField = pickField(item, ["receivable_id", "receivableId"]);
  return fromField || null;
}

export function canUnmatchReceivable(item: ReceivableListItem): boolean {
  if (isReceivableArchivedDisplayStatus(item)) return false;
  if (!pickPgReceivableId(item)) return false;
  return pickAmountPaid(item) > 0 || pickField(item, ["amount_allocated"]) !== "";
}

export function canEditReceivableAmount(item: ReceivableListItem): boolean {
  return Boolean(pickPgReceivableId(item));
}

export function canEditReceivableCustomerName(item: ReceivableListItem): boolean {
  return Boolean(pickPgReceivableId(item));
}

/** Unmatched slip / announcement / address can be removed from hot queue like TTL expiry. */
export function canDismissPendingItem(item: ReceivableListItem): boolean {
  return getDismissPendingBlockReason(item) === null;
}

/** Whether the dismiss ("ลบรายการ") action is relevant for this pending kind. */
export function isDismissPendingActionVisible(item: ReceivableListItem): boolean {
  return (
    isReceivableSlipItem(item) ||
    isReceivableAnnouncementItem(item) ||
    isReceivableAddressItem(item)
  );
}

/** Why dismiss is blocked, or null when dismiss is allowed. */
export function getDismissPendingBlockReason(
  item: ReceivableListItem,
): string | null {
  if (!isDismissPendingActionVisible(item)) {
    return "รายการประเภทนี้ลบไม่ได้";
  }
  if (isReceivableArchivedDisplayStatus(item)) {
    return "รายการนี้ออกจากคิวแล้ว — ดูข้อมูลได้อย่างเดียว";
  }
  if (!getReceivableOrderId(item)) {
    return "ไม่พบรหัสรายการในคิว";
  }
  if (canUnmatchReceivable(item) || pickAmountPaid(item) > 0) {
    return "รายการนี้มีการจับคู่แล้ว — ใช้ยกเลิกจับคู่ก่อนถ้าต้องการลบ";
  }
  return null;
}

export function canEditPendingAddress(item: ReceivableListItem): boolean {
  return Boolean(
    isReceivableAddressItem(item) &&
      getReceivableOrderId(item) &&
      !isReceivableArchivedDisplayStatus(item),
  );
}

export function canEditPendingSlipSender(item: ReceivableListItem): boolean {
  return Boolean(
    isReceivableSlipItem(item) &&
      getReceivableOrderId(item) &&
      !isReceivableArchivedDisplayStatus(item),
  );
}

export function pickSlipSenderName(item: ReceivableListItem): string {
  return (
    pickField(item, [
      "slip_sender",
      "slip_sender_name",
      "sender_name",
      "sender_name_th",
    ]) ||
    item.title?.trim() ||
    ""
  );
}

export function pickCustomerName(item: ReceivableListItem): string {
  return (
    pickField(item, ["customer_ref", "customer_name"]) ||
    item.title?.trim() ||
    ""
  );
}

export async function fetchUnmatchImpact(
  receivableId: string,
  ownerUserId: string,
  paymentId = "",
): Promise<UnmatchImpact> {
  const qs = new URLSearchParams({
    owner_user_id: ownerUserId,
    receivable_id: receivableId,
  });
  if (paymentId.trim()) qs.set("payment_id", paymentId.trim());

  const res = await marketApiFetch(
    `/api/v1/payments/corrections/unmatch/impact?${qs}`,
    { headers: { Accept: "application/json" } },
  );
  const data = (await res.json().catch(() => ({}))) as UnmatchImpact & {
    detail?: string;
  };
  if (!res.ok) {
    const detail =
      typeof data.detail === "string"
        ? data.detail
        : `Unmatch preview failed (${res.status})`;
    // Legacy 409 after last unmatch — treat as empty preview, not a hard fail.
    if (detail === "no_active_allocation" || detail.startsWith("no_active_allocation")) {
      return {
        ok: false,
        reason: "no_active_allocation",
        candidates: [],
        summary_lines: [],
      };
    }
    throw new Error(detail);
  }
  return data;
}

/** Active matched slips for a receivable — source of truth for tab badge count. */
export async function fetchUnmatchCandidates(
  receivableId: string,
  ownerUserId: string,
): Promise<UnmatchCandidate[]> {
  const qs = new URLSearchParams({
    owner_user_id: ownerUserId,
    receivable_id: receivableId,
  });
  const res = await marketApiFetch(
    `/api/v1/payments/corrections/unmatch/candidates?${qs}`,
    { headers: { Accept: "application/json" } },
  );
  const data = (await res.json().catch(() => ({}))) as {
    candidates?: UnmatchCandidate[];
    detail?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : `Unmatch candidates failed (${res.status})`,
    );
  }
  return Array.isArray(data.candidates) ? data.candidates : [];
}

export type PaymentAllocationRow = {
  allocation_id: string;
  receivable_id: string;
  amount: string;
  customer_name?: string;
  receivable_revision?: number;
  receivable_status?: string;
};

export type PaymentAllocationsResponse = {
  ok?: boolean;
  payment_id?: string;
  payment_amount?: string;
  allocated_amount?: string;
  allocations?: PaymentAllocationRow[];
};

/** Active allocations for a payment (includes closed receivables). */
export async function fetchPaymentAllocations(
  paymentId: string,
  ownerUserId: string,
): Promise<PaymentAllocationsResponse> {
  const qs = new URLSearchParams({ owner_user_id: ownerUserId });
  const res = await marketApiFetch(
    `/api/v1/payments/${encodeURIComponent(paymentId)}/allocations?${qs}`,
    { headers: { Accept: "application/json" } },
  );
  const data = (await res.json().catch(() => ({}))) as PaymentAllocationsResponse & {
    detail?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : `Payment allocations failed (${res.status})`,
    );
  }
  return data;
}

export async function postUnmatchPayment(args: {
  ownerUserId: string;
  workspaceId: string;
  receivableId: string;
  receivableItem?: ReceivableListItem | null;
  paymentId?: string;
  allocationId?: string;
  expectedRevision?: number;
  paymentOrderId?: string;
  operationKey?: string;
}): Promise<{ status?: string; after?: Record<string, unknown>; operation_key?: string }> {
  const body = {
    owner_user_id: args.ownerUserId,
    workspace_id: args.workspaceId,
    receivable_id: args.receivableId,
    payment_id: args.paymentId?.trim() || "",
    allocation_id: args.allocationId?.trim() || "",
    expected_revision:
      args.expectedRevision ??
      (args.receivableItem ? pickReceivableRevision(args.receivableItem) : 0),
    receivable_order_id: args.receivableItem
      ? getReceivableOrderId(args.receivableItem) ?? ""
      : "",
    payment_order_id: args.paymentOrderId?.trim() || "",
    operation_key: args.operationKey ?? "",
    channel: "web",
  };

  const res = await marketApiFetch(`/api/v1/payments/corrections/unmatch`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as {
    status?: string;
    detail?: string;
    error?: string;
    after?: Record<string, unknown>;
    operation_key?: string;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `Unmatch failed (${res.status})`,
    );
  }
  return data;
}

export async function fetchEditAmountImpact(
  receivableId: string,
  ownerUserId: string,
  newAmount: string,
): Promise<EditAmountImpact> {
  const qs = new URLSearchParams({
    owner_user_id: ownerUserId,
    new_amount: newAmount,
  });
  const res = await marketApiFetch(
    `/api/v1/receivables/${encodeURIComponent(receivableId)}/edit-amount/impact?${qs}`,
    { headers: { Accept: "application/json" } },
  );
  const data = (await res.json().catch(() => ({}))) as EditAmountImpact & {
    detail?: string;
  };
  if (!res.ok) {
    const reason =
      typeof data.detail === "string"
        ? data.detail
        : typeof data.reason === "string"
          ? data.reason
          : "";
    if (reason === "below_amount_paid" || data.blocked) {
      return data;
    }
    throw new Error(
      formatEditAmountErrorMessage(reason) ??
        `Edit preview failed (${res.status})`,
    );
  }
  return data;
}

export async function postEditReceivableAmount(args: {
  receivableId: string;
  ownerUserId: string;
  workspaceId: string;
  newAmount: string;
  expectedRevision?: number;
  releasePlan?: Array<{
    allocation_id?: string;
    payment_id?: string;
    amount: string;
  }>;
  operationKey?: string;
  receivableItem: ReceivableListItem;
}): Promise<{ status?: string; after?: Record<string, unknown>; operation_key?: string }> {
  const res = await marketApiFetch(
    `/api/v1/receivables/${encodeURIComponent(args.receivableId)}/edit-amount`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        owner_user_id: args.ownerUserId,
        workspace_id: args.workspaceId,
        new_amount: args.newAmount,
        expected_revision:
          args.expectedRevision ?? pickReceivableRevision(args.receivableItem),
        release_plan: args.releasePlan ?? [],
        operation_key: args.operationKey ?? "",
        channel: "web",
      }),
    },
  );
  const data = (await res.json().catch(() => ({}))) as {
    detail?: string;
    error?: string;
    after?: Record<string, unknown>;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `Edit amount failed (${res.status})`,
    );
  }
  return data;
}

export async function postEditReceivableCustomerName(args: {
  receivableId: string;
  ownerUserId: string;
  workspaceId: string;
  customerName: string;
  expectedRevision?: number;
  receivableItem: ReceivableListItem;
}): Promise<{ status?: string; after?: Record<string, unknown> }> {
  const res = await marketApiFetch(
    `/api/v1/receivables/${encodeURIComponent(args.receivableId)}/edit-customer-name`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        owner_user_id: args.ownerUserId,
        workspace_id: args.workspaceId,
        customer_name: args.customerName,
        expected_revision:
          args.expectedRevision ?? pickReceivableRevision(args.receivableItem),
        channel: "web",
      }),
    },
  );
  const data = (await res.json().catch(() => ({}))) as {
    detail?: string;
    error?: string;
    after?: Record<string, unknown>;
  };
  if (!res.ok) {
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : typeof data.error === "string"
          ? data.error
          : `Edit customer name failed (${res.status})`,
    );
  }
  return data;
}
